import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CloseIcon, UploadCloudIcon } from '../../../../public/icons';


console.log('Environment Variables:', {
  REACT_APP_CLOUDINARY_CLOUD_NAME: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
  REACT_APP_CLOUDINARY_UPLOAD_PRESET: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
  ALL_ENV_VARS: process.env
});

const AddProductModal = ({ isOpen, onClose, onSubmit, onSaveDraft }) => {
  const [productType, setProductType] = useState('standalone');
  const [formData, setFormData] = useState({
    name: '',
    shortHighlight: '',
    basePrice: '',
    stockQuantity: '',
    description: '',
    images: [],
    baseProduct: ''
  });
  const [previewImages, setPreviewImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);



    useEffect(() => {
  if (isOpen) {
    // Reset form when modal opens
    setFormData({
      name: '',
      shortHighlight: '',
      basePrice: '',
      stockQuantity: '',
      description: '',
      images: [],
      baseProduct: ''
    });
    setPreviewImages([]);
    setProductType('standalone');
    setIsSubmitting(false);
    setUploadingImages(false);
  }
}, [isOpen]);

  if (!isOpen) return null;





  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const uploadToCloudinary = async (file) => {
    const cloudinaryFormData = new FormData();
    cloudinaryFormData.append('file', file);
    cloudinaryFormData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
      cloudinaryFormData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      }
    );
    
    return response.data;
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    
    try {
      const uploadedImages = [];
      const newPreviewImages = [];
      
      for (let file of files) {
        // Create preview
        const preview = {
          file,
          preview: URL.createObjectURL(file),
          uploading: true
        };
        newPreviewImages.push(preview);
        
        // Upload to Cloudinary
        const cloudinaryResponse = await uploadToCloudinary(file);
        
        uploadedImages.push({
          url: cloudinaryResponse.secure_url,
          publicId: cloudinaryResponse.public_id,
          width: cloudinaryResponse.width,
          height: cloudinaryResponse.height,
          format: cloudinaryResponse.format
        });
        
        // Update preview to show upload complete
        preview.uploading = false;
        preview.cloudinaryUrl = cloudinaryResponse.secure_url;
      }
      
      setPreviewImages(prev => [...prev, ...newPreviewImages]);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    // Revoke object URL to prevent memory leaks
    if (previewImages[index]) {
      URL.revokeObjectURL(previewImages[index].preview);
    }
    
    const newPreviewImages = [...previewImages];
    const newImages = [...formData.images];
    
    newPreviewImages.splice(index, 1);
    newImages.splice(index, 1);
    
    setPreviewImages(newPreviewImages);
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const handleSubmit = async (isDraft = false) => {
    try {
      setIsSubmitting(true);
      
      // Prepare data to send
      const dataToSend = {
        name: formData.name,
        typeOfProduct: productType,
        basePrice: productType === 'standalone' ? parseFloat(formData.basePrice) : 0,
        stockQuantity: productType === 'standalone' ? parseInt(formData.stockQuantity) || 0 : 0,
        shortHighlight: formData.shortHighlight,
        description: formData.description,
        images: formData.images,
        status: isDraft ? 'draft' : 'active'
      };

      // Add baseProduct for collection type
      if (productType === 'collection') {
        dataToSend.baseProduct = formData.baseProduct;
      }
      
      // Call the appropriate handler
      if (isDraft && onSaveDraft) {
        await onSaveDraft(dataToSend);
      } else {
        await onSubmit(dataToSend);
      }
      
      // Reset form on success
      setFormData({
        name: '',
        shortHighlight: '',
        basePrice: '',
        stockQuantity: '',
        description: '',
        images: [],
        baseProduct: ''
      });
      setPreviewImages([]);
      setProductType('standalone');
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => handleSubmit(true);
  const handleAddProduct = () => handleSubmit(false);

  const isSubmitEnabled = formData.name && formData.description && !isSubmitting && !uploadingImages;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300 text-left"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] text-left lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b px-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">Add new Product</h2>
              <p className="text-[#6B635D] text-sm font-light mt-1">
                Manage products, collections, and how customers discover Kapta offerings.
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] "
              disabled={isSubmitting}
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Product Type Selection */}
            <div className="mb-8 space-y-4">
              <div 
                onClick={() => setProductType('collection')}
                className="cursor-pointer"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex items-start mt-1">
                    <div className={`w-6 h-6 rounded  flex items-center justify-center ${
                      productType === 'collection' 
                        ? 'bg-[#1C2A46]' 
                        : 'border-2 border-[#1C2A46]'
                    }`}>
                      {productType === 'collection' && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-[#1C2A46] text-base mb-1">Collection-based product</h3>
                    <p className="text-[#6B635D] font-normal text-sm">
                      A product that belongs under an existing base product and collection
                    </p>
                  </div>
                </div>
              </div>

              <div 
                onClick={() => setProductType('standalone')}
                className="cursor-pointer"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex items-start mt-1">
                    <div className={`w-6 h-6 rounded  flex items-center justify-center ${
                      productType === 'standalone' 
                        ? 'bg-[#1C2A46]' 
                        : 'border-2 border-[#1C2A46]'
                    }`}>
                      {productType === 'standalone' && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-[#1C2A46] text-base mb-1">Standalone product</h3>
                    <p className="text-[#6B635D] font-normal text-sm">
                      A product that exists on its own
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {productType === 'collection' ? (
              <>
                {/* Base Product Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Product name *
                  </label>
                  <input
                    type="text"
                    name="baseProduct"
                    value={formData.baseProduct}
                    onChange={handleChange}
                    placeholder="Enter base product name"
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Product name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Variant name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Yankari memory magnet"
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Product description */}
                <div className="mb-8">
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Product description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={6}
                    placeholder="enter product description..."
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none"
                    disabled={isSubmitting}
                  />
                </div>
              </>
            ) : (
              <>
                {/* Product name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Product name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="enter product name"
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Short highlight */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Short highlight
                  </label>
                  <input
                    type="text"
                    name="shortHighlight"
                    value={formData.shortHighlight}
                    onChange={handleChange}
                    placeholder="enter short highlight"
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                    disabled={isSubmitting}
                  />
                </div>

                <div className='flex justify-between gap-3'>
                  {/* Base price */}
                  <div className="mb-6 w-1/2">
                    <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                      Base price (₦) *
                    </label>
                    <input
                      type="number"
                      name="basePrice"
                      value={formData.basePrice}
                      onChange={handleChange}
                      placeholder="15000"
                      className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Stock quantity */}
                  <div className="mb-6 w-1/2">
                    <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                      Stock quantity
                    </label>
                    <input
                      type="number"
                      name="stockQuantity"
                      value={formData.stockQuantity}
                      onChange={handleChange}
                      placeholder="100"
                      className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Product description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Product description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={6}
                    placeholder="enter product description..."
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none"
                    disabled={isSubmitting}
                  />
                </div>
              </>
            )}

            {/* Product images */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Product images *
              </label>
              <div className="border-2 border-dashed border-[#DBDBDB] bg-[#F5F5F5] p-8 text-center hover:border-[#D2A146] transition-colors cursor-pointer ">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  multiple
                  className="hidden"
                  id="productImages"
                  disabled={isSubmitting || uploadingImages}
                />
                <label htmlFor="productImages" className="cursor-pointer">
                  <div className="flex justify-center items-center mb-4">
                    <UploadCloudIcon className="w-12 h-12 text-gray-400" />
                  </div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    {uploadingImages ? 'Uploading images...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-gray-500">PNG, JPG up to 5mb</p>
                </label>
              </div>
              
              {/* Image Preview Section */}
              {previewImages.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">
                    {previewImages.length} image(s) selected:
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {previewImages.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview.cloudinaryUrl || preview.preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover  border border-gray-200"
                        />
                        {preview.uploading && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center ">
                            <div className="animate-spin -full h-6 w-6 border-b-2 border-white"></div>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white -full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                          disabled={isSubmitting || preview.uploading}
                        >
                          ×
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                          {preview.file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#DBDBDB] flex gap-3">
            <button 
              onClick={handleSaveDraft}
              disabled={isSubmitting || uploadingImages}
              className="px-6 py-3 border-2 border-[#DBDBDB] text-[#1C2A46] font-medium  hover:bg-gray-50 transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save as draft'}
            </button>
            <button 
              onClick={handleAddProduct}
              disabled={!isSubmitEnabled}
              className={`px-6 py-3 font-medium  transition-colors flex-1 ${
                isSubmitEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? 'Adding...' : (productType === 'collection' ? 'Add Product' : 'Add product')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProductModal;